import { TOKEN_ADDRESSES, TOKEN_CONFIG, publicClients } from "@/config";
import { LoadingText } from "@/constants/text.enum";
import { TOKEN_IDS, type TokenId } from "@/types";
import { useApprovalStore } from "@/zustand/ApprovalStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type Hash, erc20Abi, getContract, maxUint256, parseUnits, type Address } from "viem";
import { mainnet } from "viem/chains";
import { useAccount, useWalletClient } from "wagmi";
import Big from "big.js";

export type UseTokenApprovalProps = {
    amount: string | number;
    spender: Address;
    tokenId: TokenId;
    tokenAddress?: Address;
};

type ApprovalActionOptions = {
    isExecuting?: boolean;
    amount?: string | number;
};

/**
 * Custom hook for managing token approvals.
 * @param spender - The address of the spender.
 * @param tokenId - The ID of the token to approve.
 * @param amount - The amount of tokens to approve.
 * @param tokenAddress - (Optional) The address of the token contract.
 *
 * @note If tokenAddress is provided, it will be used to create the contract instance.
 * @note Otherwise, the address will be looked up using the tokenId.
 */
export const useTokenApprovalV2 = ({ spender, tokenId, amount, tokenAddress }: UseTokenApprovalProps) => {
    const bAmount = useMemo(() => Big(amount).toFixed(TOKEN_CONFIG[tokenId].decimals, 0), [amount, tokenId]);
    const { address: owner } = useAccount();
    const { data: walletClient } = useWalletClient();
    const { allowance, setAllowance, isCheckingAllowance, setIsCheckingAllowance, isApproving, setIsApproving } =
        useApprovalStore();
    // Get token contract instance
    const tokenContract = useMemo(() => {
        if (!tokenAddress && !tokenId) {
            console.error("Missing params", {
                tokenAddress,
                tokenId,
            });
            return;
        }

        const chainId = mainnet.id;

        const contractAddress = tokenAddress ? tokenAddress : TOKEN_ADDRESSES[chainId]?.[tokenId as TokenId];

        if (!contractAddress) {
            return;
        }

        return getContract({
            address: contractAddress,
            abi: erc20Abi,
            client: {
                wallet: walletClient,
                public: publicClients[mainnet.id],
            },
        });
    }, [walletClient, walletClient?.chain, walletClient?.chain.id, tokenAddress, tokenId]);

    // Calculate amount in wei based on token decimals
    const amountAsWei = useMemo(() => {
        if (!bAmount) return;
        return parseUnits(`${bAmount}`, TOKEN_CONFIG[tokenId].decimals);
    }, [tokenId, bAmount]);

    const getAmountAsWei = useCallback(
        (targetAmount?: string | number) => {
            if (targetAmount === undefined || targetAmount === null) {
                return amountAsWei;
            }

            const normalizedAmount = Big(targetAmount).toFixed(TOKEN_CONFIG[tokenId].decimals, 0);
            return parseUnits(`${normalizedAmount}`, TOKEN_CONFIG[tokenId].decimals);
        },
        [amountAsWei, tokenId],
    );
    // Fetch and update token allowance
    const syncAllowance = useCallback(async (): Promise<bigint> => {
        if (!tokenContract || !owner || !spender) {
            setIsCheckingAllowance(false);
            setIsApproving(false);
            throw new Error(
                `Missing required parameters ${JSON.stringify({
                    owner: owner,
                    spender,
                    tokenAddress,
                    tokenId,
                })}`,
            );
        }

        try {
            setIsCheckingAllowance(true);
            const result = await tokenContract?.read.allowance([owner, spender]).then(BigInt);
            setAllowance(result);
            return result;
        } catch (error) {
            console.error("Error checking token approval:", error);
            throw new Error("Failed to check token approval");
        } finally {
            setIsCheckingAllowance(false);
        }
    }, [tokenContract, owner, spender, setAllowance, setIsCheckingAllowance, tokenAddress, tokenId]);

    /**
     * Simulates the approval transaction to check for potential errors
     */
    const simulateApprove = useCallback(async (): Promise<boolean> => {
        if (!walletClient || !tokenContract) {
            return false;
        }

        try {
            await tokenContract.simulate.approve([spender, maxUint256]);
            return true;
        } catch (error) {
            console.error("Approval simulation failed:", error);
            return false;
        }
    }, [walletClient, tokenContract, spender]);

    /**
     * Approves the token for spending
     */
    const approve = useCallback(async (): Promise<Hash> => {
        if (!walletClient || !tokenContract) {
            setIsApproving(false);
            throw new Error("Wallet client or token contract not available");
        }

        if (typeof amountAsWei !== "bigint") {
            setIsApproving(false);
            throw new Error("Amount is not defined");
        }

        try {
            setIsApproving(true);

            // If tokenId is USDT, first reduce approval to 0; required to stop USDT race condition
            // but only if allowance is greater than 0
            if (tokenId === TOKEN_IDS.USDT && allowance && allowance > 0n) {
                await getContract({
                    address: tokenContract.address,
                    abi: erc20Abi,
                    client: walletClient,
                }).write.approve([spender, 0n]);
            }

            const approveTx = await getContract({
                address: tokenContract.address,
                abi: erc20Abi,
                client: walletClient,
            }).write.approve([spender, maxUint256]);

            const receipt = await publicClients[mainnet.id].waitForTransactionReceipt({
                hash: approveTx,
                confirmations: 1,
            });

            if (receipt.status !== "success") {
                console.error("Token approval transaction failed");
                throw new Error("Token approval transaction failed");
            }

            setAllowance(maxUint256);

            return receipt.transactionHash as Hash;
        } catch (error) {
            setAllowance(undefined);
            console.error("Error approving token:", error);
            throw error;
        } finally {
            setIsApproving(false);
            setIsCheckingAllowance(false);
        }
    }, [
        walletClient,
        tokenContract,
        spender,
        amountAsWei,
        syncAllowance,
        setIsApproving,
        setIsCheckingAllowance,
        setAllowance,
    ]);

    // Generic isLoading state
    const isLoading = isApproving || isCheckingAllowance;

    // Determine if the token is approved for the required amount
    const isApproved = useMemo(() => {
        if (undefined === amountAsWei) return false;
        if (undefined === allowance) return false;

        return allowance >= amountAsWei;
    }, [amountAsWei, allowance, amount]);

    const canStart = useCallback(
        (options?: ApprovalActionOptions) => {
            if (options?.isExecuting) {
                console.error("Transaction is already in progress");
                return false;
            }

            if (isApproving) {
                console.error("Token approval is in progress");
                return false;
            }

            if (isCheckingAllowance) {
                console.error("Token allowance is being checked");
                return false;
            }

            return true;
        },
        [isApproving, isCheckingAllowance],
    );

    const ensureApproval = useCallback(
        async (options?: ApprovalActionOptions) => {
            if (!canStart(options)) return false;

            const targetAmountAsWei = getAmountAsWei(options?.amount);

            if (targetAmountAsWei === undefined) {
                throw new Error("Amount is not defined");
            }

            const currentAllowance = allowance ?? (await syncAllowance());

            if (currentAllowance < targetAmountAsWei) {
                await approve();
            }

            return true;
        },
        [canStart, getAmountAsWei, allowance, syncAllowance, approve],
    );

    const withApproval = useCallback(
        async <T>(execute: () => Promise<T>, options?: ApprovalActionOptions): Promise<T | undefined> => {
            const isReady = await ensureApproval(options);
            if (!isReady) return;

            return execute();
        },
        [ensureApproval],
    );

    const approvalLoadingText = useMemo(() => {
        if (isCheckingAllowance) return LoadingText.CHECKING_ALLOWANCE;
        if (isApproving) return LoadingText.APPROVING;
        return "";
    }, [isApproving, isCheckingAllowance]);

    const isApprovalLoading = isApproving || isCheckingAllowance;

    // Initial loader (to reset and fetch latest allowance)
    const [prevTokenId, setPrevToken] = useState<TokenId>();
    useEffect(() => {
        if (!prevTokenId || prevTokenId !== tokenId || undefined === allowance) {
            setPrevToken(tokenId);
            syncAllowance()
                .then((allowance) => {
                    if (typeof allowance === "bigint") {
                        setAllowance(allowance);
                    } else {
                        console.error("Invalid allowance");
                    }
                })
                .catch(console.error);
        }
    }, [prevTokenId, tokenId, allowance, syncAllowance, setAllowance]);

    return {
        allowance,
        isApproved,
        isApproving,
        isCheckingAllowance,
        isLoading,
        approve,
        canStart,
        ensureApproval,
        withApproval,
        approvalLoadingText,
        isApprovalLoading,
        simulateApprove,
        syncAllowance,
    };
};
